import i18next from 'i18next'
import React, { useEffect } from 'react'
import {
  IOWidget,
  StandardLocalWidget,
  Updater,
  Widget,
  any,
  async,
  componentDidCatch,
  fromJSX,
  onlyIf,
} from 'widgets-for-react'
import { activateAccount } from '../../authentication/auth_widget/activate_account'
import { isSessionValid } from '../../authentication/auth_widget/api_to_state'
import {
  AuthState,
  isLoggedInState,
} from '../../authentication/auth_widget/authState'
import { AuthApi } from '../../authentication/auth_widget/authenticationApi'
import { loginWidget } from '../../authentication/auth_widget/login'
import { logout } from '../../authentication/auth_widget/logout'
import { resetPassword } from '../../authentication/auth_widget/reset_password'
import { resetPasswordRequest } from '../../authentication/auth_widget/reset_password_request'
import { signup } from '../../authentication/auth_widget/signup'
import { LoginFlowClasses } from '../../authentication/auth_widget/style'
import {
  isLoggedIn,
  isLoggedOut,
  isVerifyAuthentification,
} from '../../authentication/auth_widget/utils'
import { userType } from '../../subscription/subscriptionApi'
import { subscriptionUpdaters } from '../../subscription/subscriptionState'
import { subscriptionWidget } from '../../subscription/subscriptionWidget'
import { DataLayer } from '../../utils/datalayer'
import { FormControls } from '../../utils/forms'
import { stopSubscriptionWidget } from '../../subscription/stopSubscription/stopSubscriptionWidget'
import {
  ComponentDidMount,
  ComponentDidUpdate,
} from '../../utils/lifecycleMethods'
import { Redirect } from '../../utils/redirect'
import { CurrentPage } from '../../utils/routes'
import { signupSuccessBannerWidget } from './banner/bannerWidget'
import { blogOverviewWidget } from './blogs/blogOverview/blogOverviewWidget'
import { collectionDetailWidget } from './collections/collectionDetail/collectionDetailWidget'
import {
  getCollections,
  loadCollections,
} from './collections/collectionsLoader/collectionsLoaderApi'
import { collectionsLoaderWidget } from './collections/collectionsLoader/collectionsLoaderWidget'
import { componentDetailWidget } from './components/componentDetail/componentDetailWidget'
import { componentEditWidget } from './components/componentEdit/componentEditWidget'
import { componentOverviewWidget } from './components/componentOverview/componentOverviewWidget'
import { initialComponentsCountLoaderState } from './components/componentsCountLoader/componentsCountLoaderState'
import { componentsCountLoaderWidget } from './components/componentsCountLoader/componentsCountLoaderWidget'
import { contactWidget } from './contact/contactWidget'
import { dashboardWidget } from './dashboard/dashboardWidget'
import { dishOverviewWidget } from './dishes/dishOverview/dishOverviewWidget'
import { initialFiltersLoaderState } from './filters/filtersLoader/filtersLoaderState'
import { filtersLoaderWidget } from './filters/filtersLoader/filtersLoaderWidget'
import { footer } from './footer/footerWidget'
import { headerWidget } from './header/headerWidget'
import { homeWidget } from './home/home.widget'
import { hubspotTrackingWidget } from './hubspotTracking/hubspotTrackingWidget'
import { knowledgeOverviewWidget } from './knowledge/knowledgeOverview/knowledgeOverviewWidget'
import { knowledgeTypeOverviewWidget } from './knowledge/knowledgeTypeOverview/knowledgeTypeOverviewWidget'
import { metadataWidget } from './metadata/metadata.widget'
import { RootLayout } from './rootLayout'
import { RootState, rootStateUpdaters } from './rootState'
import { Pages, routeUpdaters } from './routes/routesState'
import {
  isDifferentPage,
  parseArrayParam,
  parseCategoryParam,
  parsePageParam,
} from './routes/routesUtils'
import { routerWidget } from './routes/routesWidget'
import { searchWidget } from './search/searchWidget'
import { seasonalCalendarWidget } from './seasonalCalendar/seasonalCalendarWidget'
import { sharepointPageWidget } from './sharepointPage/sharepointPageWidget'
import { studentSubscriptionWidget } from './studentSubscription/studentSubscriptionWidget'
import { updatePaymentWidget } from './updatePayment/updatePaymentWidget'
import { viewListenerWidget } from './viewListener/viewListenerWidget'
import { ThankYouUpdatePaymentLayout } from './thankYouUpdatePayment/thankYouUpdatePaymentLayout'
import { UpdatePaymentRedirectLayout } from './updatePayment/redirect/updatePaymentRedirectLayout'

export const rootWidget: StandardLocalWidget<RootState> = (state) =>
  any<Updater<RootState>>([
    routerWidget(i18next.language),
    metadataWidget(state.metadataState, state.page).map(
      rootStateUpdaters.updateMetadataState
    ),
    verifySession,
    userSubscriptionsLoader(state),
    collectionsLoaderWidget(state.collectionsLoaderState).map(
      rootStateUpdaters.updateCollectionsState
    ),
    componentsCountLoaderWidget(state.componentsCountLoaderState).map(
      rootStateUpdaters.componentsCountLoaderStateUpdater
    ),
    filtersLoaderWidget(state.filtersLoaderState).map(
      rootStateUpdaters.filtersLoaderStateUpdater
    ),
    fromJSX((setState) => (
      <ComponentDidUpdate
        value={i18next.language}
        onUpdate={(oldLanguage, newLanguage) => {
          if (oldLanguage !== newLanguage)
            setState((state) => ({
              ...state,
              componentsCountLoaderState: initialComponentsCountLoaderState,
              collectionsLoaderState: loadCollections(
                getCollections,
                state.collectionsLoaderState
              ),
              filtersLoaderState: initialFiltersLoaderState,
            }))
        }}
      />
    )),
    fromJSX((setState) => (
      <ComponentDidMount
        onMount={() => {
          window.addEventListener('beforeprint', () =>
            setState((state) => ({ ...state, isPrintMode: true }))
          )
          window.addEventListener('afterprint', () =>
            setState((state) => ({ ...state, isPrintMode: false }))
          )
        }}
      />
    )),
    loadHubspotTracking(state),
    viewListenerWidget.map(rootStateUpdaters.viewListenerStateUpdater),
    isVerifyAuthentification(state.authState)
      ? fromJSX(() => <RootLayout.PageLoader />)
      : any<Updater<RootState>>([
          isFormSkeletonPage(state.page.kind, isLoggedIn(state.authState))
            ? page(state)
            : any<Updater<RootState>>([
                headerWidget(
                  state.authState,
                  state.subscriptionState.userSubscriptions,
                  state.page.kind,
                  state.page.kind === 'search'
                    ? parseCategoryParam(state.page.params.tab)
                    : 'components'
                )(state.header),
                any<Updater<RootState>>([
                  signupSuccessBannerWidget(
                    state.page,
                    state.subscriptionState.userSubscriptions
                  ),
                  page(state),
                ]).wrapHTML((element) => (
                  <RootLayout.Page
                    child={element}
                    pageKind={state.page}
                    isLoggedIn={isLoggedIn(state.authState)}
                    userType={userType(
                      state.subscriptionState.userSubscriptions
                    )}
                  />
                )),
                onlyIf(state.page.kind !== 'componentEdit', footer(state)),
              ]),
        ])
          .wrapHTML((element) => (
            <>
              <ComponentDidUpdate
                value={state.page}
                onUpdate={(oldPage, newPage) => {
                  if (isDifferentPage(oldPage, newPage)) window.scrollTo(0, 0)
                }}
              />
              {element}
            </>
          ))
          .wrapHTML((element) => {
            const fireEvents = () => {
              DataLayer.pageView()
              if (
                isLoggedIn(state.authState) &&
                state.subscriptionState.userSubscriptions.kind == 'loaded'
              ) {
                DataLayer.loggedInUserAccountType(
                  userType(state.subscriptionState.userSubscriptions)
                )
                DataLayer.loggedInUserJobTitle(
                  userType(state.subscriptionState.userSubscriptions),
                  state.subscriptionState.userSubscriptions.value.User.Id.toString(),
                  state.subscriptionState.userSubscriptions.value.User.Job?.Id.toString()
                )
              }
            }

            return (
              <>
                <ComponentDidMount onMount={fireEvents} />
                <ComponentDidUpdate
                  value={state.page}
                  onUpdate={(oldPage, newPage) => {
                    if (isDifferentPage(oldPage, newPage)) {
                      fireEvents()

                      const params = newPage.params as { [key: string]: string }

                      if (params['originPath']) {
                        var canonicalURLTag = document.querySelector(
                          "link[rel='canonical']"
                        )
                        var url = new URL(window.location.href)
                        canonicalURLTag?.setAttribute(
                          'href',
                          `${url.origin}${url.pathname}`
                        )
                      }
                    }
                  }}
                />
                {element}
              </>
            )
          }),
  ])

const verifySession = isSessionValid(AuthApi).map(
  rootStateUpdaters.updateAuthState
)

const userSubscriptionsLoader = (state: RootState) =>
  async(state.subscriptionState.userSubscriptions)
    .map(subscriptionUpdaters.userSubscriptions)
    .map(rootStateUpdaters.updateSubscriptionState)

const loadHubspotTracking = (state: RootState) =>
    hubspotTrackingWidget(
      state.hubspotTrackingState,
      state.page,
      isLoggedIn(state.authState) && isLoggedInState(state.authState)
        ? state.authState.user
        : undefined
    ).map(rootStateUpdaters.updateHubspotTrackingState)

const page: IOWidget<RootState, Updater<RootState>> = (currentState) =>
  componentDidCatch(currentState.page.kind, pageSwitch(currentState), () =>
    fromJSX(() => <RootLayout.Error />)
  )

const redirect = <T,>(to: string, butFirst?: () => void): Widget<T> =>
  fromJSX(() => <Redirect to={to} butFirst={butFirst} />)

const login = (
  authState: AuthState,
  redirectTo?: string,
  studentFlow: boolean = false
) =>
  loginWidget(
    authOptions,
    AuthApi,
    'standard',
    authState,
    redirectTo ? redirectTo : '/',
    studentFlow,
    redirectTo ? redirect(redirectTo).never<Updater<AuthState>>() : undefined
  ).map(rootStateUpdaters.updateAuthState)

export const authOptions = { translator: i18next, style: LoginFlowClasses }

function pageSwitch(state: RootState): Widget<Updater<RootState>> {
  const {
    authState,
    page,
    collectionsLoaderState,
    subscriptionState,
    isPrintMode
  } = state

  const loggedIn = isLoggedIn(authState)
  const loggedOut = isLoggedOut(authState)

  const userSubscriptionsState = subscriptionState.userSubscriptions
  const userOrDefaultLanguage =
    (userSubscriptionsState.kind == 'loaded' &&
      userSubscriptionsState.value.User.Language.Code) ||
    i18next.language

  switch (page.kind) {
    case 'homeRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(routeUpdaters(userOrDefaultLanguage).home.url + window.location.search, () =>
            i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'home':
      return homeWidget(
        loggedIn,
        userSubscriptionsState,
        collectionsLoaderState,
        state.header.searchString,
        page.params['signup-success'] == 'true'
      )(page.pageState).map(rootStateUpdaters.homeStateUpdater)
    case 'componentOverview':
      return componentOverviewWidget(
        {
          allergens: parseArrayParam(page.params.allergen),
          techniques: parseArrayParam(page.params.technique),
          appearances: parseArrayParam(page.params.appearance),
          textures: parseArrayParam(page.params.texture),
          types: parseArrayParam(page.params.type),
        },
        {
          category: page.params.category,
          subcategory: page.params.subcategory,
          ingredient: page.params.ingredient,
        },
        parsePageParam(page.params.page),
        loggedIn,
        userSubscriptionsState,
        collectionsLoaderState,
        state.filtersLoaderState,
        state.componentsCountLoaderState,
        state.viewListenerState
      )(page.pageState).map(rootStateUpdaters.componentOverviewStateUpdater)
    case 'componentDetail':
      return componentDetailWidget(
        loggedIn,
        userSubscriptionsState,
        collectionsLoaderState
      )(page.pageState).map(rootStateUpdaters.updateComponentDetailState)
    case 'componentEdit':
      return componentEditWidget(
        loggedIn,
        userSubscriptionsState,
        parseInt(page.params.componentId)
      )(page.pageState).map(rootStateUpdaters.updateComponentEditState)
    case 'blogOverview':
      return blogOverviewWidget(parsePageParam(page.params.page))(
        page.pageState
      ).map(rootStateUpdaters.updateBlogOverviewState)
    case 'blogDetail':
      return sharepointPageWidget(
        'blog',
        collectionsLoaderState,
        loggedIn,
        isPrintMode,
        userSubscriptionsState
      )(page.pageState).map(rootStateUpdaters.updatePageState)
    case 'search':
      return searchWidget(
        parseCategoryParam(page.params.tab),
        page.params.term || '',
        {
          allergens: parseArrayParam(page.params.allergen),
          techniques: parseArrayParam(page.params.technique),
          appearances: parseArrayParam(page.params.appearance),
          textures: parseArrayParam(page.params.texture),
          types: parseArrayParam(page.params.type),
        },
        parsePageParam(page.params.page),
        loggedIn,
        userSubscriptionsState,
        collectionsLoaderState,
        state.filtersLoaderState,
        state.viewListenerState
      )(page.pageState).map(rootStateUpdaters.updateSearchState)
    case 'dashboard':
      return loggedIn
        ? dashboardWidget(
            loggedIn,
            userSubscriptionsState,
            collectionsLoaderState,
            parsePageParam(page.params.page)
          )(page.pageState).map(rootStateUpdaters.updateDashboardState)
        : login(authState)
    case 'collectionDetail':
      return loggedIn
        ? collectionDetailWidget(
            page.params.collectionId,
            loggedIn,
            userSubscriptionsState,
            collectionsLoaderState,
            parsePageParam(page.params.page)
          )(page.pageState).map(rootStateUpdaters.updateCollectionDetailState)
        : login(authState)
    case 'knowledgeOverview':
      return knowledgeOverviewWidget(
        loggedIn,
        userSubscriptionsState
      )(page.pageState).map(rootStateUpdaters.updateKnowledgeOverviewState)
    case 'knowledgeTypeOverview':
      return knowledgeTypeOverviewWidget(
        loggedIn,
        userSubscriptionsState
      )(page.pageState).map(rootStateUpdaters.updateKnowledgeTypeOverviewState)
    case 'knowledgeDetail':
      return sharepointPageWidget(
        'knowledge',
        collectionsLoaderState,
        loggedIn,
        isPrintMode,
        userSubscriptionsState
      )(page.pageState).map(rootStateUpdaters.updatePageState)
    case 'seasonalCalendar':
      return seasonalCalendarWidget(
        loggedIn,
        userSubscriptionsState,
        state.viewListenerState
      )(page.pageState).map(rootStateUpdaters.updateSeasonalCalendarState)
    case 'page':
      return sharepointPageWidget(
        'page',
        collectionsLoaderState,
        loggedIn,
        isPrintMode,
        userSubscriptionsState
      )(page.pageState).map(rootStateUpdaters.updatePageState)
    case 'contact':
      return contactWidget(page.pageState).map(
        rootStateUpdaters.updateContactState
      )
    case 'dishesOverview':
      return dishOverviewWidget(parsePageParam(page.params.page))(
        page.pageState
      ).map(rootStateUpdaters.updateDishesOverviewState)
    case 'dishesDetail':
      return sharepointPageWidget(
        'dish',
        collectionsLoaderState,
        loggedIn,
        isPrintMode,
        userSubscriptionsState
      )(page.pageState).map(rootStateUpdaters.updatePageState)
    case 'accountDetails':
    case 'upgrade':
      return loggedIn
        ? subscriptionWidget(subscriptionState).map(
            rootStateUpdaters.updateSubscriptionState
          )
        : login(authState)
    case 'subscriptionSuccessRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).subscriptionSuccess.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'subscriptionCancelRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).subscriptionCancel.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'subscriptionFailureRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).subscriptionFailure.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'changePaymentMethodSuccessRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).changePaymentMethodSuccess.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'changePaymentMethodFailureRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).changePaymentMethodFailure.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'changePaymentMethodCancelRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).changePaymentMethodCancel.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'payOpenInvoiceSuccessRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).payOpenInvoiceSuccess.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'payOpenInvoiceFailureRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).payOpenInvoiceFailure.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'payOpenInvoiceCancelRedirect':
      return loggedOut || userSubscriptionsState.kind == 'loaded'
        ? redirect(
            routeUpdaters(userOrDefaultLanguage).payOpenInvoiceSuccess.url,
            () => i18next.changeLanguage(userOrDefaultLanguage)
          )
        : fromJSX(() => <RootLayout.PageLoader />)
    case 'subscriptionSuccess':
    case 'subscriptionCancel':
    case 'subscriptionFailure':
    case 'changePaymentMethodSuccess':
    case 'changePaymentMethodFailure':
    case 'changePaymentMethodCancel':
    case 'payOpenInvoiceSuccess':
    case 'payOpenInvoiceFailure':
    case 'payOpenInvoiceCancel':
      return loggedIn
        ? subscriptionWidget(subscriptionState).map(
            rootStateUpdaters.updateSubscriptionState
          )
        : login(authState)
    case 'subscriptionStopPayment':
      return loggedIn
      ? stopSubscriptionWidget(page.pageState).map(
          rootStateUpdaters.updateSubscriptionStopPaymentState
        )
      : login(authState)
    case 'register':
      return signup(
        authOptions,
        AuthApi,
        'standard',
        authState,
        FormControls.ReactRouterLink({
          path: i18next.t('footer:legalTermsUrl'),
          linkText: i18next.t('auth:signupTermsAndConditionsPost'),
          target: '_blank',
        }),
        page.params.originPath ? page.params.originPath : '/',
        'regular',
        true
      )
        .wrapHTML((child) => (
          <CanonicalSetter originPath={page.params.originPath} child={child} />
        ))
        .map(rootStateUpdaters.updateAuthState)
    case 'registerStudent':
      return signup(
        authOptions,
        AuthApi,
        'standard',
        authState,
        FormControls.ReactRouterLink({
          path: i18next.t('footer:legalTermsUrl'),
          linkText: i18next.t('auth:signupTermsAndConditionsPost'),
          target: '_blank',
        }),
        page.params.originPath ? page.params.originPath : '/',
        'student',
        true
      )
        .wrapHTML((child) => (
          <CanonicalSetter originPath={page.params.originPath} child={child} />
        ))
        .map(rootStateUpdaters.updateAuthState)
    case 'registerPromo':
      return signup(
        authOptions,
        AuthApi,
        'standard',
        authState,
        FormControls.ReactRouterLink({
          path: i18next.t('footer:legalTermsUrl'),
          linkText: i18next.t('auth:signupTermsAndConditionsPost'),
          target: '_blank',
        }),
        '/',
        'promo',
        true
      )
        .wrapHTML((child) => (
          <CanonicalSetter originPath={page.params.originPath} child={child} />
        ))
        .map(rootStateUpdaters.updateAuthState)
    case 'login':
      return loggedIn
        ? redirect(page.params.originPath ? page.params.originPath : '/')
        : login(
            authState,
            page.params.originPath,
            page.params['student-flow'] === 'true'
          ).wrapHTML((child) => (
            <CanonicalSetter
              originPath={page.params.originPath}
              child={child}
            />
          ))
    case 'logout':
      return loggedIn
        ? logout(authOptions, AuthApi, authState).map(
            rootStateUpdaters.updateAuthState
          )
        : redirect('/')
    case 'activateAccount':
      return activateAccount(authOptions, AuthApi, authState, page.params).map(
        rootStateUpdaters.updateAuthState
      )
    case 'resetPassword':
      return resetPassword(
        authOptions,
        AuthApi,
        authState,
        FormControls.ReactRouterLink({
          path: routeUpdaters(i18next.language).contact.url,
          linkText: i18next.t('auth:contactForm'),
        })
      ).map(rootStateUpdaters.updateAuthState)
    case 'requestResetPassword':
      return resetPasswordRequest(
        authOptions,
        AuthApi,
        authState,
        FormControls.ReactRouterLink({
          path: routeUpdaters(i18next.language).contact.url,
          linkText: i18next.t('auth:contactForm'),
        })
      ).map(rootStateUpdaters.updateAuthState)
    case 'studentSubscription':
      return studentSubscriptionWidget(
            loggedIn,
            userSubscriptionsState
          )(page.pageState).map(
            rootStateUpdaters.updateStudentSubscriptionState
          )
    case 'updatePayment': 
      return updatePaymentWidget(loggedIn)(subscriptionState).map(rootStateUpdaters.updateSubscriptionState)
    case 'updatePaymentRedirect': 
      return fromJSX(() => {
        let routes = routeUpdaters(i18next.language);
        return <UpdatePaymentRedirectLayout successUrl={routes.thankYouUpdatePayment.url} updatePaymentUrl={routes.updatePayment.url} />
      })
    case 'thankYouUpdatePayment': 
      return fromJSX(() => <ThankYouUpdatePaymentLayout/> )
    case 'notFound':
      return fromJSX(() => <RootLayout.Error />)
  }
}

const isFormSkeletonPage = (
  pagekind: CurrentPage<Pages>['kind'],
  isLoggedIn: boolean
) => {
  switch (pagekind) {
    case 'activateAccount':
    case 'login':
    case 'logout':
    case 'register':
    case 'resetPassword':
    case 'requestResetPassword':
    case 'upgrade':
    case 'updatePayment':
    case 'updatePaymentRedirect':
    case 'subscriptionSuccessRedirect':
    case 'subscriptionSuccess':
    case 'subscriptionFailureRedirect':
    case 'subscriptionFailure':
    case 'subscriptionCancelRedirect':
    case 'subscriptionCancel':
    case 'studentSubscription':
    case 'registerStudent':
    case 'registerPromo':
    case 'thankYouUpdatePayment':
      return true
    case 'dashboard':
    case 'collectionDetail':
    case 'accountDetails':
      return !isLoggedIn
    default:
      return false
  }
}

const CanonicalSetter = (props: {
  originPath?: string
  child: JSX.Element
}) => {
  useEffect(() => {
    if (props.originPath) {
      if (document.querySelector("link[rel='canonical']") == null) {
        const newTag = document.createElement('link')
        newTag.setAttribute('rel', 'canonical')
        var url = new URL(window.location.href)
        if (!url.host.startsWith('www.')) url.host = `www.${url.host}`
        newTag.setAttribute('href', `${url.origin}${url.pathname}`)

        document.head.appendChild(newTag)
      }
      return () => {
        const existingTag = document.querySelector("link[rel='canonical']")
        if (existingTag != null) document.head.removeChild(existingTag)
      }
    }
  }, [props.originPath])
  return props.child
}
